import { apiClient } from './client'

/**
 * Incoming webhook triggers of a Workflow published by an Application
 * (pilote-maurice wiki/architecture/workflow-webhook-trigger.md § 8).
 * The secret is only returned by create and rotateSecret.
 */

export type WorkflowTriggerStatus = 'active' | 'disabled' | 'revoked'
export type WorkflowTriggerOutcome = 'accepted' | 'duplicate' | 'rejected' | ''
export type WorkflowTriggerResponseMode = 'async' | 'sync'

export interface WorkflowTriggerInputMapping {
  mode: 'body' | 'pointers'
  fields?: Record<string, string>
  constants?: Record<string, unknown>
}

export interface WorkflowTrigger {
  schema_version: string
  trigger_id: string
  kind: string
  name: string
  application_id: string
  application_key: string
  agent_id: string
  workflow_id: string
  status: WorkflowTriggerStatus
  secret_hint?: string
  input_mapping: WorkflowTriggerInputMapping
  response_mode: WorkflowTriggerResponseMode
  sync_timeout_seconds: number
  rate_limit_per_minute: number
  path: string
  previous_secret_expires_at?: string | null
  last_call_at?: string | null
  last_outcome?: WorkflowTriggerOutcome
  last_error_code?: string
  created_at: string
  updated_at: string
  disabled_at?: string | null
  revoked_at?: string | null
}

export interface WorkflowTriggerCall {
  id: string
  received_at: string
  outcome: WorkflowTriggerOutcome
  http_status: number
  error_code?: string
  execution_id?: string
  duration_ms: number
  delivery_id?: string
  body_size: number
  count: number
}

export interface WorkflowTriggerSecretResponse {
  trigger: WorkflowTrigger
  path: string
  url: string
  secret: string
}

export interface CreateWorkflowTriggerInput {
  name: string
  workflow_id: string
  agent_id?: string
  input_mapping?: WorkflowTriggerInputMapping
  response_mode?: WorkflowTriggerResponseMode
  sync_timeout_seconds?: number
  rate_limit_per_minute?: number
}

/** Edition of a trigger: an absent field keeps its value (AGENT-842). */
export interface UpdateWorkflowTriggerInput {
  name?: string
  input_mapping?: WorkflowTriggerInputMapping
  response_mode?: WorkflowTriggerResponseMode
  sync_timeout_seconds?: number
  rate_limit_per_minute?: number
}

function triggersPath(organizationId: string, applicationId: string) {
  return `/organization/${organizationId}/applications/${applicationId}/workflow-triggers`
}

export const workflowTriggersAPI = {
  list: async (organizationId: string, applicationId: string, workflowId: string) => {
    const response = await apiClient.get<{ triggers?: WorkflowTrigger[] }>(
      triggersPath(organizationId, applicationId),
      { params: { workflow_id: workflowId } },
    )
    return Array.isArray(response?.triggers) ? response.triggers : []
  },

  create: (organizationId: string, applicationId: string, body: CreateWorkflowTriggerInput) =>
    apiClient.post<WorkflowTriggerSecretResponse>(triggersPath(organizationId, applicationId), body),

  update: (
    organizationId: string,
    applicationId: string,
    triggerId: string,
    body: UpdateWorkflowTriggerInput,
  ) =>
    apiClient.patch<{ trigger: WorkflowTrigger }>(
      `${triggersPath(organizationId, applicationId)}/${triggerId}`,
      body,
    ),

  disable: (organizationId: string, applicationId: string, triggerId: string) =>
    apiClient.post<{ trigger: WorkflowTrigger }>(
      `${triggersPath(organizationId, applicationId)}/${triggerId}/disable`,
    ),

  enable: (organizationId: string, applicationId: string, triggerId: string) =>
    apiClient.post<{ trigger: WorkflowTrigger }>(
      `${triggersPath(organizationId, applicationId)}/${triggerId}/enable`,
    ),

  rotateSecret: (organizationId: string, applicationId: string, triggerId: string) =>
    apiClient.post<WorkflowTriggerSecretResponse>(
      `${triggersPath(organizationId, applicationId)}/${triggerId}/rotate-secret`,
    ),

  revoke: (organizationId: string, applicationId: string, triggerId: string) =>
    apiClient.delete<{ trigger: WorkflowTrigger }>(
      `${triggersPath(organizationId, applicationId)}/${triggerId}`,
    ),

  listCalls: async (organizationId: string, applicationId: string, triggerId: string) => {
    const response = await apiClient.get<{ calls?: WorkflowTriggerCall[] }>(
      `${triggersPath(organizationId, applicationId)}/${triggerId}/calls`,
      { params: { limit: 20 } },
    )
    return Array.isArray(response?.calls) ? response.calls : []
  },
}
